import React, { memo, useEffect, useState } from 'react';
import { Theme } from '../../interfaces/enums/Theme.enum';
import Container from '../Utils/Container/Container';
import Grid from '../Utils/Grid/Grid';
import Col from '../Utils/Col/Col';
import FooterNavItems from './components/FooterNavItems';
import FooterSocialLinks from './components/FooterSocialLinks';
import CustomLink from '../CustomLink/CustomLink';
import { Blok } from '../../interfaces/Blok.interface';
import { StoryblokAsset } from '../../interfaces/StoryblokAsset.interface';
import { SocialLinkProps } from '../SocialLink/SocialLink.component';
import { StoryblokNode } from '../../interfaces/StoryblokNode.interface';
import { withBlokWrapper } from '../BlokWrapper/BlokWrapper';
import { LinkProps } from '../../interfaces/LinkProps.interface';
import { StoryblokComponent, storyblokEditable } from 'gatsby-source-storyblok';
import SbImage from '../SbImage/SbImage';
import FooterLogo, { FooterLogoProps } from './components/FooterLogo';
import classNames from 'classnames';
import Image from '../Image/Image';
import { useMediaQuery } from 'react-responsive';

export interface FooterBlokProps extends Blok {
  logo: StoryblokAsset;
  secondary_logo: FooterLogoProps[];
  social_links: StoryblokNode<SocialLinkProps>[];
  column_two_footer_nav_items: LinkProps[];
  column_three_footer_nav_items: LinkProps[];
  body_copy: string;
  show_widget: boolean;
  structured_data: Blok[];
}

interface FooterProps {
  blok: FooterBlokProps;
}

const Ecologi: React.FC = () => {
  return (
    <CustomLink
      url={
        'https://ecologi.com/myhomemoveconveyance?r=65e5cb356809b020f9ca1a63'
      }
      className={classNames('inline-block h-full')}
    >
      <SbImage
        asset={{
          alt: 'We plant trees with Ecologi',
          filename:
            'https://api.ecologi.com/badges/trees/65e5cb356809b020f9ca1a63?white=true&treeOnly=true',
          copyright: '',
          fieldtype: '',
          _uid: '',
          focus: '',
          id: 0,
          name: 'Ecologi',
          title: 'Ecologi',
        }}
        options={{
          className: 'h-[160px] !w-[213px]',
          width: 213,
          height: 160,
        }}
      />
    </CustomLink>
  );
};

const Footer: React.FC<FooterProps> = ({ blok }) => {
  const {
    logo,
    social_links,
    secondary_logo,
    column_two_footer_nav_items,
    column_three_footer_nav_items,
    body_copy,
    show_widget,
    structured_data,
  } = blok;

  const md = useMediaQuery({
    query: '(max-width: 1023px)',
  });

  const [isQuoteEmbed, setIsQuoteEmbed] = useState(false);
  useEffect(() => {
    if (sessionStorage.getItem('quote-embed') === 'true') {
      setIsQuoteEmbed(true);
    }
  }, []);

  return (
    <footer
      className={classNames('sticky bottom-0 bg-surface/background py-20', {
        hidden: isQuoteEmbed,
      })}
      {...storyblokEditable(blok)}
    >
      {/*-- structured data. this will only show if one has been added, and if they have at least one social link --*/}
      {structured_data && social_links && (
        <>
          {structured_data?.map((blok: Blok) => (
            <StoryblokComponent
              blok={{ ...blok, social_links: social_links }}
              key={blok._uid}
            />
          ))}
        </>
      )}
      <Container>
        <Grid>
          <Col span={{ lg: 3 }}>
            <div className="space-y-10">
              <CustomLink
                url={''}
                label={''}
                target="_self"
                className="block w-[200px] h-[55px]"
              >
                {logo?.filename && (
                  <SbImage
                    asset={logo}
                    options={{ width: 200, height: 55, sizes: { base: 400 } }}
                  />
                )}
              </CustomLink>
              <div className="sm:grid grid-cols-2 lg:block space-y-6 sm:space-y-0 lg:space-y-10">
                <FooterSocialLinks links={social_links} />
                {secondary_logo.length && <FooterLogo {...secondary_logo[0]} />}
              </div>
            </div>
          </Col>
          <Col span={{ sm: 6, lg: 3 }}>
            {column_two_footer_nav_items && (
              <FooterNavItems items={column_two_footer_nav_items} />
            )}
          </Col>
          <Col span={{ sm: 6, lg: 3 }}>
            {column_three_footer_nav_items && (
              <FooterNavItems items={column_three_footer_nav_items} />
            )}
          </Col>
          <>
            {!md && show_widget && (
              <Col span={{ md: 6, lg: 3 }}>
                <Ecologi />
              </Col>
            )}
          </>
          <Col span={{ default: 12 }}>
            <div className="sm:border-t sm:border-system/white sm:pt-6 space-y-10 sm:space-y-0 sm:grid grid-cols-2 lg:grid-cols-1">
              {md && show_widget && <Ecologi />}
              {body_copy && (
                <p className="text-text/body text-sm border-t border-system/white pt-6 sm:pt-0 sm:border-none">
                  {body_copy}
                </p>
              )}
            </div>
          </Col>
        </Grid>
      </Container>
    </footer>
  );
};

export default memo(withBlokWrapper(Footer, Theme.DARK));
